import type { RunEtaPayload } from "@the-drop/types";

/**
 * Inline ETA + stops-ahead badge for the transporter dashboard's selected-driver footer. Internal-
 * only surface — `run.eta` is denylisted from customer namespaces (PR #374) so it's safe to
 * render the run's `stopsAhead` here alongside the window.
 *
 * Mirrors the dispensary component of the same name; the two surfaces want the same shape but
 * different chrome (the dispensary version stacks tight inside a table row; this version is
 * inline + compact inside the map panel's footer card).
 */
export type RunEtaInlineProps = {
  runEta: RunEtaPayload | undefined;
  /** Specific order's stop to surface. When null/undefined → uses `stopsAhead = 0` (the next stop). */
  orderId?: string | null;
};

export function RunEtaInline({ runEta, orderId }: RunEtaInlineProps) {
  const stop = runEta ? pickStop(runEta, orderId ?? null) : null;
  if (!stop) return null;
  const windowLabel = `${formatEtaEdge(stop.etaStart)} – ${formatEtaEdge(stop.etaEnd)}`;
  const aheadLabel =
    stop.stopsAhead === 0 ? "next stop" : `+${stop.stopsAhead} ${stop.stopsAhead === 1 ? "stop" : "stops"} ahead`;
  const isRunningLate = isWindowInPast(stop.etaEnd);
  return (
    <span className="flex flex-wrap items-baseline gap-x-1.5">
      <span className={`text-xs font-semibold ${isRunningLate ? "text-amber-700" : "text-td-ink"}`}>
        {windowLabel}
      </span>
      <span className="text-2xs text-td-muted">{aheadLabel}</span>
    </span>
  );
}

function pickStop(
  runEta: RunEtaPayload,
  preferredOrderId: string | null,
): RunEtaPayload["stops"][number] | null {
  if (preferredOrderId) {
    const match = runEta.stops.find((s) => s.orderId === preferredOrderId);
    if (match) return match;
  }
  const next = runEta.stops.find((s) => s.stopsAhead === 0);
  if (next) return next;
  return runEta.stops[0] ?? null;
}

function isWindowInPast(iso: string): boolean {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  return t < Date.now();
}

function formatEtaEdge(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const meridiem = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const mm = String(minutes).padStart(2, "0");
  return `${hours}:${mm} ${meridiem}`;
}
